<template>

  <!-- Page Header -->
  <q-toolbar
    :style="styles"
    :class="classes"
    v-scroll="scrolled"
    class="page-header bs-px">

    <!-- Logo -->
    <q-btn
      flat
      dense
      to="/"
      stretch
      @click="$scrollTo()"
      class="page-header__icon page-header__logo">

      <img
        class="page-header__logo-icon"
        src="/images/logo/logo-light-dense.svg"/>

      <svg
        fill="currentColor"
        viewBox="0 0 86 37"
        class="page-header__logo-text xs-hide">

        <use
          xlink:href="/images/logo/logo-symbols.svg#text"/>

      </svg>

    </q-btn>

    <!-- Hamburger -->
    <q-btn
      flat
      dense
      stretch
      @click="$emit('drawer')"
      class="page-header__icon_dense page-header__hamburger">

      <img
        src="/images/icons/hamburger.svg">

    </q-btn>

    <q-space/>

    <!-- Menu -->
    <page-menu
      no-dots
      v-if="showMenu"
      :items="menuItems"
      class="xs-hide sm-hide"
      @expand="$emit('drawer')"/>

    <q-space/>

    <!-- Account -->
    <q-btn
      flat
      dense
      stretch
      @click.stop="$refs.search.show()"
      class="page-header__icon_dense page-header__search">

      <svg
        width="29"
        height="29"
        fill="currentColor"
        viewBox="0 0 48 48"
        class="page-header__search-icon">

        <use
          xlink:href="/images/icons/icon-symbols.svg?v1#search"/>

      </svg>

    </q-btn>

    <!-- Account -->
    <q-btn
      flat
      dense
      stretch
      :to="accountLink"
      class="page-header__icon_dense page-header__account">

      <svg
        width="29"
        height="29"
        fill="currentColor"
        viewBox="0 0 48 48"
        class="page-header__account-icon">

        <use
          xlink:href="/images/icons/icon-symbols.svg?v1#account"/>

      </svg>

    </q-btn>

    <page-search
      ref="search"/>

  </q-toolbar>

</template>

<script>

  import debounce from 'lodash/debounce';
  import {mapState, mapGetters} from 'vuex';
  import PageMenu from '@src/components/page/page-menu';
  import PageSearch from '@src/components/page/page-search';

  export default {
    name: 'page-header',
    props: {
      menuItems: {
        type: Array,
        default: () => [],
      },
    },
    components: {
      PageMenu,
      PageSearch,
    },
    data() {
      return {
        color: '',
        textColor: '',
        isScrolled: false,
      };
    },
    computed: {
      ...mapState('vlVendors', {
        vendor: 'item',
      }),
      ...mapState('header', {
        startColor: 'startColor',
        startTextColor: 'startTextColor',
        scrolledColor: 'scrolledColor',
        scrolledTextColor: 'scrolledTextColor',
        showMenu: 'showMenu',
        showShadow: 'showShadow',
      }),
      ...mapGetters('session', {
        isLoggedIn: 'isLoggedIn',
      }),
      isVendor() {

        return this.vendor &&
          this.$route.name === 'public-vendor';
      },
      isPremium() {

        return this.vendor
          && this.vendor.premium;
      },
      styles() {

        return {
          color: this.textColor,
          backgroundColor: this.color,
        };
      },
      classes() {

        return {
          'has-vendor': this.isVendor,
          'has-shadow': this.showShadow,
          'has-scrolled': this.isScrolled,
          [`bg-${this.color}`]: this.color,
          [`text-${this.textColor}`]: this.textColor,
        };
      },
      scrolled() {

        return debounce(
          this.updateColors,
          100,
          {leading: true, trailing: true},
        );
      },
      accountLink() {

        return this.isLoggedIn
          ? '/dialog/logout'
          : '/dialog/login';
      },
    },
    methods: {
      updateColors(scrolled) {

        this.isScrolled = scrolled;

        this.color = scrolled
          ? this.scrolledColor
          : this.startColor;

        this.textColor = scrolled
          ? this.scrolledTextColor
          : this.startTextColor;
      },
    },
    mounted() {

      this.scrolled(
        window.scrollTop,
      );
    },
    watch: {
      '$route'() {

        this.scrolled(
          window.scrollTop,
        );
      },
    },
  };

</script>

<style lang="scss">

  $cls: page-header;

  $img-py: 7px;
  $img-px: 30px;
  $img-px-sm: 20px;

  $img-py-dense: 7px;
  $img-px-dense: 20px;
  $img-px-sm-dense: 15px;

  .page-header {
    color: $font-color;
    background-color: white;
    height: $page-header-height;
    transition: background-color 150ms ease-out;

    &.has-shadow,
    &.has-scrolled {
      box-shadow: rgba(0, 0, 0, .08) 0 3px 10px, rgba(0, 0, 0, .04) 0 1px 2px;
    }

    @media (max-width: $breakpoint-sm) {
      height: $page-header-height-sm;
    }

    @media (max-width: $breakpoint-xs) {
      height: $page-header-height-xs;
    }

    &__icon,
    &__icon_dense {

      .q-btn__wrapper {
        padding: 0;
        display: block;
      }

      .q-btn__content {
        height: 100%;
      }
    }

    &__icon {
      padding: $img-py $img-px;

      &:first-child {
        margin-left: -$img-px;
      }

      &:last-child {
        margin-right: -$img-px;
      }
    }

    @media (max-width: $breakpoint-sm-max) {

      &__icon {
        padding: $img-py $img-px-sm;

        &:first-child {
          margin-left: -$img-px-sm;
        }

        &:last-child {
          margin-right: -$img-px-sm;
        }
      }
    }

    &__icon_dense {
      padding: $img-py-dense $img-px-dense;

      &:first-child {
        margin-left: -$img-px-dense;
      }

      &:last-child {
        margin-right: -$img-px-dense;
      }
    }

    @media (max-width: $breakpoint-sm-max) {

      &__icon_dense {
        padding: $img-py-dense $img-px-sm-dense;

        &:first-child {
          margin-left: -$img-px-sm-dense;
        }

        &:last-child {
          margin-right: -$img-px-sm-dense;
        }
      }
    }

    &__logo {

      .q-btn__content {
        display: flex;
        flex-wrap: nowrap;
      }

      &-icon {
        height: 75%;

        @media (max-width: $breakpoint-xs-max) {
          height: 70%;
        }
      }

      &-text {
        height: 66%;
        margin-top: -2px;
        margin-left: 1.5rem;

        @media (max-width: $breakpoint-sm-max) {
          margin-top: -1px;
          margin-left: 1rem;
        }
      }
    }

    &__hamburger img {
      height: 52%;

      @media (max-width: $breakpoint-sm-max) {
        height: 65%;
      }
    }
  }

</style>
